import {useState, useEffect} from 'react';

import {Button} from 'shells/button/button';
import {MultiAddTextField} from 'shells/form/multi-add-textfield/multi-add-textfield';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import type {SaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {SAVE_STATE_KEYS, DISABLED_PATHS_LIMIT} from '../consts';
import type {SaveStateKey} from '../consts';

type Props = {
    disabledPaths: string[];
    handleSave: (updates: ChatSettingsLookAndFeelInput, toastKey: SaveStateKey) => Promise<any>;
    toastState: SaveState;
    savingToastKey: SaveStateKey | null | undefined;
    isLoading: boolean;
};

export function DisabledPathsConfigurationSection(props: Props) {
    const [disabledPaths, setDisabledPaths] = useState<string[]>(props.disabledPaths);

    useEffect(() => {
        setDisabledPaths(props.disabledPaths);
    }, [props.disabledPaths]);

    if (props.isLoading) {
        return <LoadingText height={200} width={100} />;
    }

    return (
        <LabelWithContent
            label='What URLs should chat be disabled on?'
            labelPopoverProps={{
                info: 'Custom URLS to disable chat, like a signup page, or billing form',
                bodyColor: 'dark',
            }}
            labelButton={
                props.disabledPaths.length > 0 ? (
                    <Button
                        variant='text'
                        size='small'
                        onClick={() =>
                            props.handleSave({disabledPaths: []}, SAVE_STATE_KEYS.DISABLED_PATHS)
                        }
                    >
                        Clear
                    </Button>
                ) : undefined
            }
            state={
                props.savingToastKey === SAVE_STATE_KEYS.DISABLED_PATHS
                    ? props.toastState
                    : 'initial'
            }
            spacing={4}
            content={
                <MultiAddTextField
                    items={disabledPaths}
                    onChange={(newDisabledPaths) => {
                        setDisabledPaths(newDisabledPaths);
                        props.handleSave(
                            {
                                disabledPaths: newDisabledPaths
                                    .map((path) => path.trim())
                                    .filter((path) => path && path.trim() !== ''),
                            },
                            SAVE_STATE_KEYS.DISABLED_PATHS
                        );
                    }}
                    itemConfig={{
                        getValue: (item) => item,
                        setValue: (item, value) => value,
                        createNew: () => '',
                        getKey: (item, index) => `path-${index}`,
                    }}
                    isDebouncedSave={true}
                    debouncedSaveDelay={1000}
                    placeholder='Enter a path to disable chat on'
                    addButtonText='Add path'
                    minItems={1}
                    maxItems={DISABLED_PATHS_LIMIT}
                    textFieldProps={{
                        multiline: true,
                        shouldGrowVertically: true,
                    }}
                />
            }
        />
    );
}
